import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PROJECT_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Pencil, Plus, Trash2, X, Copy, ShieldCheck, GripVertical } from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Categories and items shipped from the server have no client-stable
// identity, so move/remove ops would shuffle React keys and trash the
// inputs' focus state. `_uid()` mints a per-render-session id we attach
// on load and strip before save. `assignUids` is recursive so nested
// items get them too — keeps the React-key invariant ("stable, unique,
// non-index") satisfied across reorders, edits, and re-renders.
let _uidCounter = 0;
const _uid = () => `${Date.now().toString(36)}-${++_uidCounter}`;
function assignUids(tpl) {
  return {
    ...tpl,
    categories: (tpl.categories || []).map((c) => ({
      ...c,
      _uid: c._uid || _uid(),
      items: (c.items || []).map((it) => ({ ...it, _uid: it._uid || _uid() })),
    })),
  };
}
function stripUids(tpl) {
  return {
    ...tpl,
    categories: (tpl.categories || []).map(({ _uid: _cu, ...c }) => ({
      ...c,
      items: (c.items || []).map(({ _uid: _iu, ...it }) => it),
    })),
  };
}

function emptyTemplate() {
  return assignUids({
    name: "New Template",
    project_type: "microwave",
    customer: "",
    categories: [{ name: "Pre-Construction", items: [{ key: "site_overview", label: "Site Overview" }] }],
    is_default: false,
  });
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 60);
}

export default function AdminTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState("list"); // "list" | "edit"
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/templates/photos");
    setTemplates(data);
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setDraft(emptyTemplate());
    setEditingId(null);
    setMode("edit");
  };
  const openEdit = (tpl) => {
    // Mint client-side _uid keys so categories/items keep stable React
    // identity through reorders and edits. Stripped before save.
    setDraft(assignUids(JSON.parse(JSON.stringify(tpl))));
    setEditingId(tpl.id);
    setMode("edit");
  };
  const cloneTemplate = (tpl) => {
    const copy = JSON.parse(JSON.stringify(tpl));
    copy.name = `${tpl.name} (Copy)`;
    copy.is_default = false;
    delete copy.id;
    setDraft(assignUids(copy));
    setEditingId(null);
    setMode("edit");
  };
  const backToList = () => {
    setDraft(null);
    setEditingId(null);
    setMode("list");
  };

  const remove = async (tpl) => {
    if (tpl.is_default) {
      toast.error("Cannot delete the built-in default template");
      return;
    }
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/templates/photos/${tpl.id}`);
      toast.success("Template deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        // Server never sees the client-only _uid markers — strip them
        // before the round trip.
        ...stripUids(draft),
        customer: draft.customer || null,
        project_type: draft.project_type || null,
      };
      if (editingId) {
        await api.put(`/templates/photos/${editingId}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates/photos", payload);
        toast.success("Template created");
      }
      backToList();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "edit" && draft) {
    return (
      <AppShell>
        <button
          onClick={backToList}
          className="text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 mb-3"
          data-testid="tpl-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back to templates
        </button>
        <TemplateEditor draft={draft} setDraft={setDraft} onSave={save} busy={busy} editingId={editingId} />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "Templates" }]} />
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="admin-templates-heading">Photo Templates</h1>
        </div>
        <Button onClick={startNew} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="admin-tpl-new">
          <Plus className="w-4 h-4 mr-1.5" /> NEW TEMPLATE
        </Button>
      </div>

      <Card className="bg-slate-900 border-slate-800 mb-4">
        <CardContent className="p-4 text-xs text-slate-400 leading-snug">
          Templates define required photos per <b>project type</b> and (optionally) <b>customer</b>. When a project is opened, the app picks the most specific match: customer + project_type wins, then project_type, then the built-in default.
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.length === 0 && <div className="text-slate-400 col-span-full p-4">No templates yet.</div>}
        {templates.map((tpl) => {
          const totalItems = (tpl.categories || []).reduce((s, c) => s + (c.items || []).length, 0);
          return (
            <Card key={tpl.id} className="bg-slate-900 border-slate-800" data-testid={`tpl-card-${tpl.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{tpl.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {tpl.project_type ? <span className="font-mono">{tpl.project_type}</span> : "any type"}
                      {tpl.customer ? <> · <span className="font-mono">{tpl.customer}</span></> : ""}
                    </div>
                  </div>
                  {tpl.is_default && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-950/40 border border-emerald-900 px-1.5 py-0.5 rounded">
                      <ShieldCheck className="w-3 h-3" /> Default
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-300 mt-2 font-mono">
                  {tpl.categories?.length || 0} categories · {totalItems} required items
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} className="border-slate-700 bg-slate-950 hover:bg-slate-800" data-testid={`tpl-edit-${tpl.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => cloneTemplate(tpl)} className="border-slate-700 bg-slate-950 hover:bg-slate-800" data-testid={`tpl-clone-${tpl.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate
                  </Button>
                  {!tpl.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => remove(tpl)} className="text-red-400 hover:text-red-300 hover:bg-slate-800" data-testid={`tpl-delete-${tpl.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </AppShell>
  );
}

function TemplateEditor({ draft, setDraft, onSave, busy, editingId }) {
  const updateField = (k, v) => setDraft({ ...draft, [k]: v });
  const updateCat = (idx, k, v) => {
    const cats = draft.categories.slice();
    cats[idx] = { ...cats[idx], [k]: v };
    setDraft({ ...draft, categories: cats });
  };
  const addCat = () => setDraft({ ...draft, categories: [...draft.categories, { _uid: _uid(), name: "New Category", items: [] }] });
  const removeCat = (idx) => setDraft({ ...draft, categories: draft.categories.filter((_, i) => i !== idx) });
  const moveCat = (idx, dir) => {
    const cats = draft.categories.slice();
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    [cats[idx], cats[j]] = [cats[j], cats[idx]];
    setDraft({ ...draft, categories: cats });
  };

  const addItem = (catIdx, label) => {
    const trimmed = (label || "").trim();
    if (!trimmed) return;
    const cats = draft.categories.slice();
    cats[catIdx] = {
      ...cats[catIdx],
      items: [...cats[catIdx].items, { _uid: _uid(), key: slugify(trimmed), label: trimmed, description: "" }],
    };
    setDraft({ ...draft, categories: cats });
  };
  const removeItem = (catIdx, itemIdx) => {
    const cats = draft.categories.slice();
    cats[catIdx] = { ...cats[catIdx], items: cats[catIdx].items.filter((_, i) => i !== itemIdx) };
    setDraft({ ...draft, categories: cats });
  };
  const updateItem = (catIdx, itemIdx, patch) => {
    const cats = draft.categories.slice();
    const items = cats[catIdx].items.slice();
    const merged = { ...items[itemIdx], ...patch };
    // The `key` is the requirement_key stored on every uploaded photo.
    // If we re-slugify it on every label edit, renaming a template item
    // silently orphans every photo that was uploaded under the old key
    // across every project that uses this template. Freeze the key
    // once it exists — only newly-added items (which start with an
    // empty key) get one derived from the label.
    if ("label" in patch && !items[itemIdx].key) {
      merged.key = slugify(patch.label) || merged.key;
    }
    items[itemIdx] = merged;
    cats[catIdx] = { ...cats[catIdx], items };
    setDraft({ ...draft, categories: cats });
  };
  const moveItem = (catIdx, itemIdx, dir) => {
    const cats = draft.categories.slice();
    const items = cats[catIdx].items.slice();
    const j = itemIdx + dir;
    if (j < 0 || j >= items.length) return;
    [items[itemIdx], items[j]] = [items[j], items[itemIdx]];
    cats[catIdx] = { ...cats[catIdx], items };
    setDraft({ ...draft, categories: cats });
  };

  // ---------- Unified DnD ----------
  // Single DndContext so items can be dragged BETWEEN categories.
  // Each draggable declares its `type` (category | item) via useSortable
  // data, and this handler routes accordingly.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const findItemLocation = (itemId) => {
    for (let ci = 0; ci < draft.categories.length; ci++) {
      const ii = draft.categories[ci].items.findIndex((it) => it._uid === itemId);
      if (ii >= 0) return { ci, ii };
    }
    return null;
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeType = active.data?.current?.type;

    // Category-level drag: reorder within `draft.categories`.
    if (activeType === "category") {
      const oldIndex = draft.categories.findIndex((c) => c._uid === active.id);
      // `over.id` might be an item id (dropped onto a child of another
      // category, common with closestCorners) or a category-droppable
      // sentinel. Resolve to the containing category so the reorder
      // still works.
      let newIndex = draft.categories.findIndex((c) => c._uid === over.id);
      if (newIndex < 0) {
        const overType = over.data?.current?.type;
        if (overType === "item") {
          const loc = findItemLocation(over.id);
          if (loc) newIndex = loc.ci;
        } else if (overType === "category-droppable") {
          newIndex = draft.categories.findIndex((c) => c._uid === over.data.current.catId);
        }
      }
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      setDraft({ ...draft, categories: arrayMove(draft.categories, oldIndex, newIndex) });
      return;
    }

    // Item drag. Source is always known via active.data. Destination
    // depends on what `over` is: another item (drop next to it), or
    // a category container (drop at the end of that category).
    if (activeType === "item") {
      const src = findItemLocation(active.id);
      if (!src) return;
      const overType = over.data?.current?.type;
      let dstCi = -1;
      let dstIi = -1;
      if (overType === "item") {
        const loc = findItemLocation(over.id);
        if (!loc) return;
        dstCi = loc.ci;
        dstIi = loc.ii;
      } else if (overType === "category-droppable") {
        // Dropped on the category container itself (e.g. an empty
        // category or the space below the last item). Append.
        dstCi = draft.categories.findIndex((c) => c._uid === over.data.current.catId);
        if (dstCi < 0) return;
        dstIi = draft.categories[dstCi].items.length;
      } else {
        return;
      }
      if (src.ci === dstCi) {
        // Same-category reorder.
        const items = arrayMove(draft.categories[src.ci].items, src.ii, dstIi);
        const cats = draft.categories.slice();
        cats[src.ci] = { ...cats[src.ci], items };
        setDraft({ ...draft, categories: cats });
      } else {
        // Cross-category move.
        const cats = draft.categories.slice();
        const [moved] = cats[src.ci].items.slice(src.ii, src.ii + 1);
        cats[src.ci] = {
          ...cats[src.ci],
          items: cats[src.ci].items.filter((_, i) => i !== src.ii),
        };
        const dstItems = cats[dstCi].items.slice();
        dstItems.splice(dstIi, 0, moved);
        cats[dstCi] = { ...cats[dstCi], items: dstItems };
        setDraft({ ...draft, categories: cats });
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="tpl-editor">
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-wide text-slate-300">Template Name</Label>
            <Input value={draft.name} onChange={(e) => updateField("name", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tpl-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-300">Project Type</Label>
            <Select value={draft.project_type || ""} onValueChange={(v) => updateField("project_type", v)}>
              <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tpl-project-type">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-300">Customer (optional)</Label>
            <Input value={draft.customer || ""} onChange={(e) => updateField("customer", e.target.value)} placeholder="Leave blank to apply to all customers" className="mt-1.5 bg-slate-950 border-slate-700" data-testid="tpl-customer" />
          </div>
        </CardContent>
      </Card>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <SortableContext
          items={draft.categories.map((c) => c._uid)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4" data-testid="tpl-categories-list">
            {draft.categories.map((cat, ci) => (
              <SortableCategoryCard
                key={cat._uid}
                cat={cat}
                ci={ci}
                renderCategory={(catObj, ciObj, dragHandleProps) => (
                  <CardContent className="p-5 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        {...dragHandleProps}
                        className="text-slate-500 hover:text-blue-300 cursor-grab active:cursor-grabbing touch-none shrink-0"
                        aria-label="Drag to reorder category"
                        title="Drag to reorder category (or use ↑/↓)"
                        data-testid={`tpl-cat-${ciObj}-drag`}
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                      <Input
                        value={catObj.name}
                        onChange={(e) => updateCat(ciObj, "name", e.target.value)}
                        className="bg-slate-950 border-slate-700 font-display font-bold flex-1 min-w-[180px]"
                        data-testid={`tpl-cat-name-${ciObj}`}
                      />
                      <Button size="sm" variant="ghost" onClick={() => moveCat(ciObj, -1)} className="text-slate-400 hover:bg-slate-800" disabled={ciObj === 0}>↑</Button>
                      <Button size="sm" variant="ghost" onClick={() => moveCat(ciObj, +1)} className="text-slate-400 hover:bg-slate-800" disabled={ciObj === draft.categories.length - 1}>↓</Button>
                      <Button size="sm" variant="ghost" onClick={() => removeCat(ciObj)} className="text-red-400 hover:bg-slate-800" data-testid={`tpl-cat-delete-${ciObj}`}>
                        <Trash2 className="w-4 h-4"/>
                      </Button>
                    </div>
                    <ItemEditor
                      catId={catObj._uid}
                      items={catObj.items}
                      onAdd={(label) => addItem(ciObj, label)}
                      onRemove={(idx) => removeItem(ciObj, idx)}
                      onUpdate={(idx, patch) => updateItem(ciObj, idx, patch)}
                      onMove={(idx, dir) => moveItem(ciObj, idx, dir)}
                      testidPrefix={`tpl-cat-${ciObj}`}
                    />
                  </CardContent>
                )}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex flex-wrap gap-2">
        <Button onClick={addCat} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="tpl-add-cat">
          <Plus className="w-4 h-4 mr-1.5"/> ADD CATEGORY
        </Button>
        <Button onClick={onSave} disabled={busy} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="tpl-save">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
          {editingId ? "SAVE TEMPLATE" : "CREATE TEMPLATE"}
        </Button>
      </div>
    </div>
  );
}

function SortableCategoryCard({ cat, ci, renderCategory }) {
  // Whole-card sortable — the outer <Card> gets the transform, and
  // the drag handle receives listeners via `renderCategory`'s third
  // argument so a user grabbing anywhere else on the card (e.g. an
  // input field) doesn't accidentally start a drag. `data.type` tags
  // this draggable so the top-level DndContext handler can route it.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: cat._uid,
    data: { type: "category" },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  const dragHandleProps = { ...attributes, ...listeners };
  return (
    <Card ref={setNodeRef} style={style} className="bg-slate-900 border-slate-800" data-testid={`tpl-cat-${ci}`}>
      {renderCategory(cat, ci, dragHandleProps)}
    </Card>
  );
}

function SortableItemRow({ it, idx, itemsLen, catId, onRemove, onUpdate, onMove, testidPrefix }) {
  // Individual item row wired up for dnd-kit. `useSortable` provides
  // both the container props (position transform + transition) and
  // the drag-handle listeners; we attach `listeners` only to the
  // GripVertical handle so the label/description text stays fully
  // editable (dragging on the input would otherwise start a drag).
  // `data.type/catId` tag this draggable so the top-level DndContext
  // handler can route it (item vs category) and knows the source
  // category for cross-category moves.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: it._uid || it.key,
    data: { type: "item", catId },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-slate-950 border border-slate-800 rounded p-2"
      data-testid={`${testidPrefix}-item-${idx}`}
    >
      <div className="flex items-center gap-2">
        {/* Drag handle — pointer/keyboard listeners live here only so
            the inputs below are still text-editable. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="text-slate-500 hover:text-blue-300 cursor-grab active:cursor-grabbing touch-none shrink-0"
          aria-label="Drag to reorder"
          title="Drag to reorder (or use ▲/▼)"
          data-testid={`${testidPrefix}-item-${idx}-drag`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        {/* ▲/▼ fallback — keyboard-friendly and works on iOS Safari
            where touch-drag can be finicky. */}
        <div className="flex flex-col shrink-0 -my-1">
          <button
            type="button"
            onClick={() => onMove?.(idx, -1)}
            disabled={idx === 0}
            className="text-slate-500 hover:text-blue-300 disabled:opacity-30 disabled:hover:text-slate-500 h-4 leading-none text-xs"
            aria-label="Move up"
            title="Move up"
            data-testid={`${testidPrefix}-item-${idx}-up`}
          >
            ▲
          </button>
          <button
            type="button"
            onClick={() => onMove?.(idx, +1)}
            disabled={idx === itemsLen - 1}
            className="text-slate-500 hover:text-blue-300 disabled:opacity-30 disabled:hover:text-slate-500 h-4 leading-none text-xs"
            aria-label="Move down"
            title="Move down"
            data-testid={`${testidPrefix}-item-${idx}-down`}
          >
            ▼
          </button>
        </div>
        <Input
          value={it.label}
          onChange={(e) => onUpdate(idx, { label: e.target.value })}
          placeholder="Photo name (e.g. Ground Bars)"
          className="bg-transparent border-0 h-7 px-0 focus-visible:ring-0 font-medium"
          data-testid={`${testidPrefix}-item-${idx}-label`}
        />
        <span className="text-[10px] text-slate-500 font-mono shrink-0">{it.key}</span>
        <button onClick={() => onRemove(idx)} className="text-slate-500 hover:text-red-400" aria-label="Remove" data-testid={`${testidPrefix}-item-${idx}-remove`}>
          <X className="w-3.5 h-3.5"/>
        </button>
      </div>
      <textarea
        value={it.description || ""}
        onChange={(e) => onUpdate(idx, { description: e.target.value })}
        rows={2}
        placeholder="Field guidance for the tech (e.g. Wide shot showing all ground bars, jumper terminations, and labeling)..."
        className="mt-1 w-full bg-transparent border-0 text-xs text-slate-300 placeholder:text-slate-500 focus:outline-none resize-y leading-snug"
        data-testid={`${testidPrefix}-item-${idx}-desc`}
      />
    </div>
  );
}

function ItemEditor({ items, catId, onAdd, onRemove, onUpdate, onMove, testidPrefix }) {
  const [newLabel, setNewLabel] = useState("");
  // Category-level droppable target — lets a user drop an item onto
  // an empty category (or the space below the last item) to append.
  // `data.type/catId` are read in the parent DndContext handler.
  const { setNodeRef, isOver } = useDroppable({
    id: `cat-drop-${catId}`,
    data: { type: "category-droppable", catId },
  });
  const ids = items.map((it) => it._uid || it.key);
  return (
    <div className="space-y-2">
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`grid grid-cols-1 gap-2 min-h-[40px] rounded transition-colors ${
            isOver ? "bg-blue-950/30 ring-1 ring-blue-800" : ""
          }`}
          data-testid={`${testidPrefix}-items-list`}
        >
          {items.length === 0 && (
            <div className="text-xs text-slate-600 italic px-2 py-3 text-center border border-dashed border-slate-800 rounded">
              Drop an item here, or add one below.
            </div>
          )}
          {items.map((it, idx) => (
            <SortableItemRow
              key={it._uid || it.key}
              it={it}
              idx={idx}
              itemsLen={items.length}
              catId={catId}
              onRemove={onRemove}
              onUpdate={onUpdate}
              onMove={onMove}
              testidPrefix={testidPrefix}
            />
          ))}
        </div>
      </SortableContext>
      <div className="flex gap-2">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(newLabel); setNewLabel(""); } }}
          placeholder="Add a required photo (e.g. Ground Bars)…"
          className="bg-slate-950 border-slate-700 h-9"
          data-testid={`${testidPrefix}-item-input`}
        />
        <Button size="sm" onClick={() => { onAdd(newLabel); setNewLabel(""); }} className="bg-blue-700 hover:bg-blue-600 h-9" data-testid={`${testidPrefix}-item-add`}>
          <Plus className="w-4 h-4"/>
        </Button>
      </div>
    </div>
  );
}
